import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Container } from "@/components/Section";
import PageHero from "@/components/PageHero";
import MultiInterestSelector from "@/components/MultiInterestSelector";
import ReferralSourceSelector from "@/components/ReferralSourceSelector";
import SEOHead from "@/components/SEOHead";
import { TOK_INTEREST_OPTIONS, pageSource, deviceSource } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_GROUPS = [
  { value: "under-16", label: "Under 16" },
  { value: "16-25", label: "16 – 25" },
  { value: "adult", label: "Adult (26+)" },
  { value: "parent-guardian", label: "Parent / guardian" },
];

export default function Join() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    age_group: "adult",
    interests: [],
    location: "",
    notes: "",
    referral_source: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    if (!form.interests.length) {
      toast.error("Pick at least one interest so we know which door to open.");
      return;
    }
    if (!form.referral_source) {
      toast.error("Please tell us where you heard about TOK.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/join`, {
        ...form,
        page_source: pageSource(),
        device_source: deviceSource(),
      });
      setDone(true);
      toast.success("Welcome to the Temple. We'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <SEOHead
        title="Join the Temple · Sign up · Temple of Kulture"
        description="A single sign-up that opens the whole TOK ecosystem — early access to immersive shows, LEVEL UP intakes, Ubuntu Retreats, Home Education places, festivals and member-only rituals."
        type="website"
      />
      <PageHero
        eyebrow="Sign up"
        title="Join the Temple"
        subtitle="A single sign-up that opens the whole ecosystem — early access to immersive shows, festivals, LEVEL UP intake windows, Home Education places, Ubuntu retreats, socials and member-only rituals."
      />

      <Container className="py-16 sm:py-24 max-w-3xl">
        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-amber-400/40 bg-amber-400/5 p-10 text-center"
            data-testid="join-success"
          >
            <CheckCircle2 className="h-10 w-10 text-amber-400 mx-auto" />
            <h2 className="tok-headline-md mt-4">You&apos;re in.</h2>
            <p className="mt-3 text-zinc-300 max-w-xl mx-auto">
              We&apos;ll send the next door opening to{" "}
              <span className="text-amber-400">{form.email}</span>. In the
              meantime, take a wander through what&apos;s already alive.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/whats-on"
                className="tok-cta-label inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black px-6 py-3 transition-colors"
                data-testid="join-success-whats-on"
              >
                See what&apos;s on
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <button
                onClick={() => nav("/")}
                className="tok-cta-label inline-flex items-center gap-2 border border-amber-400/40 hover:border-amber-400 text-amber-400 px-6 py-3 transition-colors"
              >
                Back home
              </button>
            </div>
          </motion.div>
        ) : (
          <form
            onSubmit={submit}
            className="space-y-8"
            data-testid="join-form"
            noValidate
          >
            <p className="tok-eyebrow">Step 1 of 1</p>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name *">
                <input
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  required
                  data-testid="join-name"
                  className={INPUT}
                />
              </Field>
              <Field label="Email *">
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  required
                  data-testid="join-email"
                  className={INPUT}
                />
              </Field>
              <Field label="Phone (optional)">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  data-testid="join-phone"
                  className={INPUT}
                />
              </Field>
              <Field label="Where you're based">
                <input
                  type="text"
                  value={form.location}
                  onChange={set("location")}
                  placeholder="e.g. Medway, London…"
                  data-testid="join-location"
                  className={INPUT}
                />
              </Field>
            </div>

            <Field label="Age group *">
              <div
                className="flex flex-wrap gap-2"
                data-testid="join-age-group"
              >
                {AGE_GROUPS.map((a) => (
                  <button
                    type="button"
                    key={a.value}
                    onClick={() =>
                      setForm((f) => ({ ...f, age_group: a.value }))
                    }
                    data-testid={`join-age-${a.value}`}
                    className={`tok-cta-label px-4 py-2.5 border transition-colors ${
                      form.age_group === a.value
                        ? "bg-amber-400 text-black border-amber-400"
                        : "border-white/10 hover:border-amber-400 hover:text-amber-400 text-zinc-300"
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </Field>

            <MultiInterestSelector
              label="What are you drawn to? Pick as many as you like *"
              options={TOK_INTEREST_OPTIONS}
              value={form.interests}
              onChange={(interests) =>
                setForm((f) => ({ ...f, interests }))
              }
              testIdPrefix="join-interest"
              required
            />

            <ReferralSourceSelector
              value={form.referral_source}
              onChange={(v) =>
                setForm((f) => ({ ...f, referral_source: v }))
              }
              testIdPrefix="join-referral"
            />

            <Field label="Anything we should know? (optional)">
              <textarea
                rows={4}
                value={form.notes}
                onChange={set("notes")}
                data-testid="join-notes"
                placeholder="A specific show, a child you're enrolling, a partnership idea…"
                className={INPUT}
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              data-testid="join-submit"
              className="tok-cta-label inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-8 py-4 transition-colors"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {submitting ? "Sending…" : "Enter the Temple"}
            </button>
            <p className="text-xs text-zinc-500">
              We&apos;ll email you only when something is genuinely relevant —
              no spam. Submissions are reviewed by the TOK team at{" "}
              <span className="text-amber-400">info@templeofkulture.co.uk</span>.
            </p>
          </form>
        )}
      </Container>
    </div>
  );
}

const INPUT =
  "w-full bg-black border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-amber-400 transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.32em] font-bold text-zinc-400">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
